import { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { getWork, getWorksByCategory, works } from "@/mocks/works";
import { getCategory } from "@/mocks/categories";
import WorkCard from "@/components/feature/WorkCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function WorkDetail() {
  const { id } = useParams();
  const work = getWork(Number(id));
  const category = work ? getCategory(work.categorySlug) : undefined;

  usePageMeta(
    work ? `${work.title} - 火影同人动漫` : "作品未找到 - 火影同人动漫",
    work ? work.summary : "没有找到对应的作品。",
  );

  const related = useMemo(() => {
    if (!work) return [];
    const same = getWorksByCategory(work.categorySlug).filter((w) => w.id !== work.id);
    if (same.length >= 4) return same.slice(0, 4);
    const others = works.filter((w) => w.id !== work.id && w.categorySlug !== work.categorySlug);
    return [...same, ...others].slice(0, 4);
  }, [work]);

  if (!work || !category) {
    return (
      <div className="container-x py-24 text-center">
        <i className="ri-emotion-sad-line text-4xl text-background-300" />
        <h1 className="mt-4 font-heading text-xl font-black text-foreground-950">作品未找到</h1>
        <p className="mt-2 text-sm text-foreground-600">这部火影同人作品不存在或已被移除。</p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium whitespace-nowrap text-background-50 hover:bg-primary-600"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: work.title,
    description: work.summary,
    inLanguage: "zh-CN",
    author: { "@type": "Person", name: work.author },
    dateModified: work.updatedAt,
    url: `https://chakra321.github.io/work/${work.id}`,
    image: work.cover,
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <section className="border-b border-background-200 bg-background-100">
        <div className="container-x py-8">
          <Breadcrumb
            crumbs={[
              { label: `${category.name}类同人`, to: `/category/${category.slug}` },
              { label: work.title },
            ]}
          />

          <div className="mt-6 flex flex-col gap-6 md:flex-row">
            <img
              src={work.cover}
              alt={`${work.title} 火影同人封面`}
              title={`${work.title} 火影同人动漫`}
              className="w-full max-w-xs self-center rounded-lg object-cover object-top md:self-start"
            />
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`rounded-full px-3 py-1 text-xs font-medium text-background-50 ${
                    work.status === "连载中" ? "bg-primary-500" : "bg-secondary-500"
                  }`}
                >
                  {work.status}
                </span>
                <span className="rounded-full bg-background-200/70 px-3 py-1 text-xs font-medium text-foreground-700">
                  {category.name}类
                </span>
                {work.tags.map((t) => (
                  <span
                    key={t}
                    className="rounded-full bg-background-50 px-3 py-1 text-xs text-foreground-600"
                  >
                    {t}
                  </span>
                ))}
              </div>

              <h1 className="mt-4 font-heading text-2xl font-black leading-tight text-foreground-950 md:text-3xl">
                {work.title}
              </h1>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
                {work.summary}
              </p>

              <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
                {[
                  ["作者", work.author],
                  ["章节", `${work.chapters}话`],
                  ["热度", `${(work.heat / 1000).toFixed(1)}k`],
                  ["更新", work.updatedAt],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    className="rounded-md border border-background-200 bg-background-50 p-3 text-center"
                  >
                    <p className="text-xs text-foreground-500">{label}</p>
                    <p className="mt-1 truncate text-sm font-semibold text-foreground-900">{value}</p>
                  </div>
                ))}
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-2">
                <span className="flex items-center gap-1 text-sm text-accent-600">
                  <i className="ri-star-fill" /> 综合评分 {work.rating.toFixed(1)}
                </span>
                <span className="text-xs text-foreground-400">|</span>
                <span className="flex items-center gap-1 text-sm text-foreground-600">
                  <i className="ri-eye-line" /> {work.views.toLocaleString()} 次浏览
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="container-x py-10 md:py-14">
        <h2 className="font-heading text-lg font-bold text-foreground-950">作品简介</h2>
        <p className="mt-4 max-w-3xl text-sm leading-7 text-foreground-700 md:text-base">
          《{work.title}》是一部{category.name}类火影同人作品，由 {work.author} 创作，目前已更新至第{" "}
          {work.chapters} 话。作品围绕{work.summary}整部作品设定清晰、节奏紧凑，既能满足同人爱好者对火影世界的想象，也在人物塑造与剧情推进上有着不错的完成度。
        </p>
        <p className="mt-4 max-w-3xl text-sm leading-7 text-foreground-700 md:text-base">
          如果你是{category.name}题材的爱好者，这部作品值得一试。我们也会持续跟进更新进度，让你不错过任何一章新剧情，追更不迷路。
        </p>
      </section>

      <section className="border-t border-background-200 bg-background-100 py-10 md:py-14">
        <div className="container-x">
          <div className="flex items-end justify-between gap-4">
            <h2 className="font-heading text-xl font-black text-foreground-950">相关推荐</h2>
            <Link
              to={`/category/${category.slug}`}
              className="flex items-center gap-1 text-sm font-medium whitespace-nowrap text-foreground-600 hover:text-primary-700"
            >
              更多{category.name}类
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {related.map((w) => (
              <WorkCard key={w.id} work={w} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}